import { cn } from "cn";
import { Check, FileText, ImageIcon, Loader2, RotateCcw } from "lucide-react";
import * as React from "react";
import { Sheet } from "./sheet";

// An attached file as a compact tile, the height of a chip (44px on phones,
// 40px from 1024px), for the pre-chart file tray; the extension's
// attachments can reuse it. A 32px thumbnail for images, an icon with the
// page count for PDFs and documents, and its state on it: a spinner while it
// uploads, a check once the assistant uses it, dimmed with a label once its
// add was undone. The name is a short caption and the tooltip.

export type FileThumbKind = "image" | "pdf" | "document";
export type FileThumbState =
  | "queued"
  | "uploading"
  | "ready"
  | "in-use"
  | "undone"
  | "error";

export interface FileThumbProps
  extends Omit<React.ComponentPropsWithRef<"button">, "children"> {
  name: string;
  kind: FileThumbKind;
  // An object URL (or data URL) of the image itself.
  src?: string | null;
  // PDFs: the number of pages, when known.
  pages?: number | null;
  state: FileThumbState;
  // Short labels shown on the tile (translated by the caller).
  labels: {
    uploading: string;
    inUse: string;
    undone: string;
    error: string;
    pages?: (count: number) => string;
  };
}

// A status badge on the thumbnail's bottom-right edge, a third of it past
// the circle: a solid fill and a 2px ring in the tile's own surface (the
// panel, the hover surface on hover), so it reads on any picture.
const BADGE =
  "absolute flex items-center justify-center rounded-full shadow-v3-hairline ring-2 ring-v3-panel transition-colors group-hover/thumb:ring-v3-surface-2";

export const FileThumb = React.forwardRef<HTMLButtonElement, FileThumbProps>(
  function FileThumb(
    { name, kind, src, pages, state, labels, className, type, ...props },
    ref,
  ) {
    const busy = state === "uploading" || state === "queued";
    const Icon = kind === "image" ? ImageIcon : FileText;
    const status =
      state === "uploading" || state === "queued"
        ? labels.uploading
        : state === "in-use"
          ? labels.inUse
          : state === "undone"
            ? labels.undone
            : state === "error"
              ? labels.error
              : null;
    return (
      <button
        ref={ref}
        aria-busy={busy || undefined}
        className={cn(
          "group/thumb flex h-11 w-32 shrink-0 items-center gap-2 rounded-full bg-v3-panel py-1.5 pl-1.5 pr-3 font-v3 text-v3-text shadow-v3-hairline-strong outline-none transition-colors hover:bg-v3-surface-2 focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:h-10 lg:py-1 lg:pl-1",
          className,
        )}
        data-slot="file-thumb"
        data-state={state}
        title={name}
        type={type ?? "button"}
        {...props}
      >
        <span className="relative size-8 shrink-0">
          <span
            className={cn(
              "flex size-8 items-center justify-center overflow-hidden rounded-full bg-v3-surface-2 shadow-v3-hairline-strong",
              state === "undone" && "opacity-55",
            )}
          >
            {src && kind === "image" ? (
              <img
                alt=""
                className="size-full object-cover"
                draggable={false}
                src={src}
              />
            ) : (
              <Icon
                aria-hidden
                className="size-4 text-v3-text-2"
                strokeWidth={1.9}
              />
            )}
            {busy && (
              <span
                aria-hidden
                className="absolute inset-0 flex items-center justify-center bg-[color-mix(in_srgb,var(--v3-panel)_55%,transparent)]"
              >
                <Loader2 className="size-4 animate-spin text-v3-brand" />
              </span>
            )}
          </span>
          {state === "in-use" && (
            <span
              aria-hidden
              className={cn(
                BADGE,
                "-bottom-1 -right-1 size-4 bg-v3-brand text-white",
              )}
              data-badge="in-use"
            >
              <Check className="size-2.5" strokeWidth={3.5} />
            </span>
          )}
          {state === "error" && (
            <span
              aria-hidden
              className={cn(
                BADGE,
                "-bottom-1 -right-1 size-4 bg-v3-rec text-white",
              )}
              data-badge="error"
            >
              <span className="text-[11px] font-bold leading-none">!</span>
            </span>
          )}
          {kind === "pdf" && (
            <span
              aria-hidden
              className={cn(
                BADGE,
                "-left-1.5 -top-1 h-3.5 px-1 bg-v3-ink text-[7.5px] font-bold uppercase leading-none tracking-wide text-v3-on-ink",
              )}
              data-badge="pdf"
            >
              PDF
            </span>
          )}
        </span>
        <span className="flex min-w-0 flex-col items-start text-left leading-tight">
          <span
            className={cn(
              "max-w-full truncate text-[12.5px] font-medium",
              state === "undone" &&
                "text-v3-text-3 line-through decoration-v3-text-3/60",
            )}
          >
            {name}
          </span>
          {(state === "undone" ||
            (kind === "pdf" && pages && labels.pages)) && (
            <span className="flex items-center gap-0.5 text-[11px] text-v3-text-3">
              {state === "undone" ? (
                <>
                  <RotateCcw
                    aria-hidden
                    className="size-2.5"
                    strokeWidth={2.5}
                  />
                  {labels.undone}
                </>
              ) : (
                pages && labels.pages?.(pages)
              )}
            </span>
          )}
        </span>
        {/* "Undone" is already on the tile: read once, not twice. */}
        {status && state !== "undone" && (
          <span className="sr-only">{status}</span>
        )}
      </button>
    );
  },
);

// The tiles in one row at every width, so the tray is never taller than one
// chip. Tiles have one width (128px), so the row shows as many whole tiles
// as fit and then a "+N" pill in the same row, the height of a tile; it is
// never laid over a tile. The pill opens a sheet with every file (a tile
// there opens that file as it does in the row).
// The tile width (w-32).
export const FILE_THUMB_WIDTH = 128;
const GAP = 6;
const MORE_WIDTH = 48;

export const visibleTiles = (
  count: number,
  width: number,
): { shown: number; more: number } => {
  // No layout yet (or in tests): every tile.
  if (width <= 0 || count * FILE_THUMB_WIDTH + (count - 1) * GAP <= width)
    return { shown: count, more: 0 };
  const shown = Math.max(
    1,
    Math.floor((width - MORE_WIDTH) / (FILE_THUMB_WIDTH + GAP)),
  );
  return { shown, more: count - shown };
};

export function FileTray({
  children,
  className,
  moreLabel,
  allTitle,
  closeLabel,
  ...props
}: React.ComponentPropsWithRef<"ul"> & {
  // The accessible name of the "+N" pill, e.g. (n) => `${n} more files`.
  moreLabel: (count: number) => string;
  // The sheet with every file: its title and close label.
  allTitle: string;
  closeLabel: string;
}): React.JSX.Element {
  const box = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  const [all, setAll] = React.useState(false);
  React.useEffect(() => {
    const element = box.current;
    if (!element) return;
    const measure = (): void => setWidth(element.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const items = React.Children.toArray(children);
  const { shown, more } = visibleTiles(items.length, width);
  return (
    <div ref={box} data-slot="file-tray">
      <ul
        className={cn("flex gap-1.5 overflow-hidden py-0.5", className)}
        {...props}
      >
        {items.slice(0, shown)}
        {more > 0 && (
          <li className="shrink-0">
            <button
              aria-label={moreLabel(more)}
              className="flex h-11 min-w-12 items-center justify-center rounded-full bg-v3-panel px-3 font-v3 text-[13px] font-semibold tabular-nums text-v3-text shadow-v3-hairline-strong outline-none transition-colors hover:bg-v3-surface-2 focus-visible:ring-2 focus-visible:ring-v3-brand-ring lg:h-10"
              data-testid="file-tray-more"
              type="button"
              onClick={() => setAll(true)}
            >
              +{more}
            </button>
          </li>
        )}
      </ul>
      <Sheet
        closeLabel={closeLabel}
        open={all}
        testId="file-tray-all"
        title={allTitle}
        onOpenChange={setAll}
      >
        <ul
          className="flex flex-wrap gap-1.5 px-5 pb-4"
          // A tile opens its file: this list closes first.
          onClickCapture={() => setAll(false)}
        >
          {items}
        </ul>
      </Sheet>
    </div>
  );
}
